import React, { useState } from 'react';
import { motion } from 'framer-motion';

const PromptIntelligence = ({ data }) => {
  const [activeTab, setActiveTab] = useState('overview');
  if (!data) return null;

  const getPriorityColor = (p) => ({ High: '#ef4444', Medium: '#f97316', Low: '#22c55e' }[p] || '#d1d5db');
  const getOptColor = (o) => ({ Optimized: '#10b981', 'Partially Optimized': '#eab308', 'Not Optimized': '#ef4444' }[o] || '#d1d5db');
  const getQualityColor = (q) => ({ Excellent: '#10b981', Good: '#22c55e', Fair: '#eab308', Poor: '#ef4444' }[q] || '#d1d5db');
  const getDiffColor = (d) => ({ Easy: '#10b981', Medium: '#eab308', Hard: '#ef4444' }[d] || '#d1d5db');

  const renderScoreGauge = () => {
    const score = data.promptScore || 0;
    const circ = 2 * Math.PI * 60;
    const off = circ - (score / 100) * circ;
    const color = score >= 70 ? '#10b981' : score >= 40 ? '#eab308' : '#ef4444';
    return (
      <svg viewBox="0 0 150 150" style={{ width: 150, height: 150, display: 'block', margin: '0 auto' }}>
        <circle cx="75" cy="75" r="60" fill="none" stroke="#1f2937" strokeWidth="10" />
        <circle cx="75" cy="75" r="60" fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round" transform="rotate(-90, 75, 75)" style={{ transition: 'stroke-dashoffset 1.5s ease-out' }} />
        <text x="75" y="68" textAnchor="middle" fill="#fff" fontSize="26" fontWeight="bold">{score}</text>
        <text x="75" y="88" textAnchor="middle" fill="#d1d5db" fontSize="11">/ 100</text>
      </svg>
    );
  };

  const renderCategoryDonut = () => {
    const cats = data.promptCategories || [];
    if (!cats.length) return null;
    const total = cats.reduce((s, c) => s + c.percentage, 0);
    let cum = 0;
    const r = 60, circ = 2 * Math.PI * r;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
        <svg viewBox="0 0 150 150" style={{ width: 130, height: 130 }}>
          {cats.map((c, i) => {
            const dl = (c.percentage / total) * circ;
            const doff = -(cum / total) * circ;
            cum += c.percentage;
            return <circle key={i} cx="75" cy="75" r={r} fill="none" stroke={c.color} strokeWidth="14" strokeDasharray={`${dl} ${circ - dl}`} strokeDashoffset={doff} transform="rotate(-90, 75, 75)" />;
          })}
          <text x="75" y="72" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="bold">{data.totalPrompts || 0}</text>
          <text x="75" y="85" textAnchor="middle" fill="#d1d5db" fontSize="8">prompts</text>
        </svg>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {cats.map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
              <span style={{ color: '#d1d5db', fontSize: 14 }}>{c.category} ({c.count})</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderOptimizationBars = () => {
    const opts = data.promptOptimization || [];
    if (!opts.length) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {opts.map((o, i) => (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#d1d5db', fontSize: 16, fontWeight: 600 }}>{o.area}</span>
              <span style={{ color: '#d1d5db', fontSize: 15 }}>{o.currentScore}%  <span style={{ color: '#10b981' }}>{o.potentialScore}%</span></span>
            </div>
            <div style={{ background: '#1f2937', borderRadius: 6, height: 10, overflow: 'hidden', position: 'relative' }}>
              <div style={{ width: `${o.potentialScore}%`, height: '100%', background: '#1f3a2f', borderRadius: 6, position: 'absolute' }} />
              <motion.div initial={{ width: 0 }} animate={{ width: `${o.currentScore}%` }} transition={{ duration: 1, delay: i * 0.15 }}
                style={{ height: '100%', borderRadius: 6, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', position: 'relative', zIndex: 1 }} />
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
              {o.actions?.map((a, j) => (
                <span key={j} style={{ background: '#1f2937', color: '#d1d5db', padding: '2px 6px', borderRadius: 8, fontSize: 13 }}>{a}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'prompts', label: 'Prompts & Patterns' },
    { id: 'platforms', label: 'Platforms & Optimization' }
  ];

  return (
    <div style={{ color: '#fff' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 600,
              background: activeTab === tab.id ? '#6366f1' : '#1f2937', color: activeTab === tab.id ? '#fff' : '#d1d5db', transition: 'all 0.2s' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            <div style={{ background: '#111827', borderRadius: 12, padding: 20, textAlign: 'center' }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Prompt Score</h4>
              {renderScoreGauge()}
            </div>
            <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Key Metrics</h4>
              {[{ label: 'Total Prompts', value: data.totalPrompts || 0 },
                { label: 'Avg Complexity', value: data.avgComplexity || 'N/A' },
                { label: 'Growth', value: data.promptGrowth || 'N/A' }
              ].map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < 2 ? '1px solid #1f2937' : 'none' }}>
                  <span style={{ color: '#d1d5db', fontSize: 16 }}>{m.label}</span>
                  <span style={{ color: '#6366f1', fontSize: 16, fontWeight: 600 }}>{typeof m.value === 'number' ? m.value.toLocaleString() : m.value}</span>
                </div>
              ))}
            </div>
            <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Prompt Categories</h4>
              {renderCategoryDonut()}
            </div>
          </div>

          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Emerging Prompt Trends</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
              {(data.emergingPrompts || []).map((ep, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 14, border: '1px solid #1f2937' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{ep.prompt}</span>
                    <span style={{ color: '#10b981', fontSize: 15, fontWeight: 700 }}>{ep.growth}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <span style={{ background: getPriorityColor(ep.relevance === 'High' ? 'Low' : ep.relevance === 'Low' ? 'High' : 'Medium') + '22', color: getPriorityColor(ep.relevance === 'High' ? 'Low' : ep.relevance === 'Low' ? 'High' : 'Medium'), padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>
                      {ep.relevance} Relevance
                    </span>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 14 }}>{ep.action}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Prompt Optimization Areas</h4>
            {renderOptimizationBars()}
          </div>
        </motion.div>
      )}

      {activeTab === 'prompts' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24, overflowX: 'auto' }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Top AI Prompts</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(data.topPrompts || []).map((p, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 14, borderLeft: `3px solid ${getOptColor(p.optimizationLevel)}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                    <span style={{ color: '#d1d5db', fontSize: 16, fontWeight: 600, flex: 1, minWidth: 200 }}>"{p.prompt}"</span>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <span style={{ background: '#1f2937', color: '#6366f1', padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>{p.category}</span>
                      <span style={{ background: '#1f2937', color: p.frequency === 'High' ? '#ef4444' : p.frequency === 'Medium' ? '#f97316' : '#22c55e', padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>{p.frequency}</span>
                      {p.brandMention && <span style={{ background: '#10b98122', color: '#10b981', padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>Brand </span>}
                    </div>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 14, marginBottom: 4 }}>AI Response: {p.aiResponse}</p>
                  <span style={{ color: getOptColor(p.optimizationLevel), fontSize: 14, fontWeight: 600 }}>{p.optimizationLevel}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Prompt Patterns</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
              {(data.promptPatterns || []).map((pp, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 14, border: '1px solid #1f2937' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{pp.pattern}</span>
                    <span style={{ color: getPriorityColor(pp.brandOpportunity === 'High' ? 'Low' : pp.brandOpportunity === 'Low' ? 'High' : 'Medium'), fontSize: 14, fontWeight: 600 }}>{pp.brandOpportunity} Opportunity</span>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 15, marginBottom: 8 }}>{pp.description}</p>
                  {pp.examples?.map((ex, j) => (
                    <p key={j} style={{ color: '#6366f1', fontSize: 14, padding: '3px 0', fontStyle: 'italic' }}> "{ex}"</p>
                  ))}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {activeTab === 'platforms' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Platform Behavior Analysis</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
              {(data.platformBehavior || []).map((pb, i) => {
                const colors = { ChatGPT: '#10b981', Claude: '#f97316', Perplexity: '#8b5cf6', 'Google AI': '#3b82f6', 'Bing Chat': '#06b6d4' };
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                    style={{ background: '#0d1117', borderRadius: 10, padding: 16, borderTop: `3px solid ${colors[pb.platform] || '#6366f1'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>{pb.platform}</span>
                      <span style={{ background: '#1f2937', color: colors[pb.platform] || '#6366f1', padding: '3px 10px', borderRadius: 10, fontSize: 15, fontWeight: 600 }}>{pb.brandVisibility}%</span>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ color: '#d1d5db', fontSize: 14 }}>Visibility</span>
                        <span style={{ color: '#d1d5db', fontSize: 14 }}>{pb.brandVisibility}%</span>
                      </div>
                      <div style={{ background: '#1f2937', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                        <motion.div initial={{ width: 0 }} animate={{ width: `${pb.brandVisibility}%` }} transition={{ duration: 1, delay: i * 0.15 }}
                          style={{ height: '100%', borderRadius: 4, background: colors[pb.platform] || '#6366f1' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                      <span style={{ background: getQualityColor(pb.responseQuality) + '22', color: getQualityColor(pb.responseQuality), padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>
                        {pb.responseQuality} Response
                      </span>
                    </div>
                    <p style={{ color: '#d1d5db', fontSize: 14, marginBottom: 4 }}><strong style={{ color: '#d1d5db' }}>Style:</strong> {pb.promptStyle}</p>
                    <p style={{ color: '#6366f1', fontSize: 14, fontStyle: 'italic' }}>{pb.keyInsight}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Recommendations</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(data.recommendations || []).map((rec, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 14, border: '1px solid #1f2937' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{rec.title}</span>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <span style={{ background: getPriorityColor(rec.priority) + '22', color: getPriorityColor(rec.priority), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{rec.priority}</span>
                      <span style={{ background: getDiffColor(rec.difficulty) + '22', color: getDiffColor(rec.difficulty), padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>{rec.difficulty}</span>
                    </div>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 15, marginBottom: 6 }}>{rec.description}</p>
                  <p style={{ color: '#6366f1', fontSize: 14, fontStyle: 'italic' }}>Expected: {rec.expectedImpact}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PromptIntelligence;
